import { internalLogger } from '../../../interface/v1/clientOsLogger';
import bindAllMethods from '../../../utils/bindAllMethods';
import injectOneTrust from '../utils/injectOneTrust';

import { SetServiceDependencies } from '../../../infra/startup/types';
import { IAnalyticsService } from '../../AnalyticsService';
import { ValueStoreFacade } from '../../JWeb/facades';
import { INavigationService } from '../../navigationService';

import {
  OPT_IN_GROUP_VALUE,
  OPT_OUT_GROUP_VALUE
} from '../../AnalyticsService';
import { ReservedValueStoreKeys } from '../../JWeb/JWebEnums';
import { RouteServiceInputType, RoutesService } from '../../RoutesService';
import IApplicationService from '../../applicationService/IApplicationService';
import {
  LOGGEDIN_PATHNAME,
  LOGGEDOUT_PATHNAME,
  LOGIN_PATHNAME
} from '../../session/constants';
import {
  EventHandlerType,
  getOptanonConsentObjectValues,
  registerOptanonDataLayerEventHandler
} from '../utils/optanonUtils';
import { ISessionService } from '../../session';
import { IABTestingService } from '../../ABTestingService';
import { IRouteStageService } from '../../RouteStageService';

import { ConsentUpdateEventData } from './IConsentStrategy';
import { BaseConsentStrategy } from './BaseConsentStrategy';
import { ConsentStrategyParams } from './IConsentStrategy';
import { OptanonWindow } from './types';

/**
 * Web consent strategy implementation
 * Handles OneTrust/Optanon consent management for web environments.
 * This stragegy will set the One Trust Banner in the html file.
 */
export class WebConsentStrategy extends BaseConsentStrategy {
  private _analyticsService: IAnalyticsService;
  private _navigationService: INavigationService;
  private _applicationService: IApplicationService;
  private _routesService: RoutesService;
  private _sessionService: ISessionService;
  private _abTestingService: IABTestingService;
  private _routeStageService: IRouteStageService;

  private _strategyName = 'WebConsentStrategy';
  private _isNative: boolean;
  private _isEmailGathering: boolean;
  private _globalInjectConsentBanner: boolean;
  private _lastOptInGroups: string;

  constructor(params: ConsentStrategyParams) {
    super();
    this._isEmailGathering = params.isEmailGathering;
    this._isNative = params.isNative;
    this._globalInjectConsentBanner = params.globalInjectConsentBanner;
  }

  public isSupported(): boolean {
    return !this._isNative;
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    this._navigationService = services.navigationService;
    this._analyticsService = services.analyticsService;
    this._applicationService = services.applicationService;
    this._routesService = services.routesService;
    this._sessionService = services.sessionService;
    this._abTestingService = services.abTestingService;
    this._routeStageService = services.routeStageService;
    this._eventService = services.eventService;

    bindAllMethods(this);
  }

  public async init(): Promise<void> {
    if (!this.isSupported()) {
      internalLogger.warn(
        'WebConsentStrategy: Not supported in current environment'
      );
      return;
    }
    internalLogger.debug?.('WebConsentStrategy initializing...');

    this._registerOnDataLayer();
    this.applyConsentBanner();

    // Initialize _lastOptInGroups
    this._lastOptInGroups = (
      window as unknown as OptanonWindow
    )._O?.getOptInGroups();
  }

  public async refresh(): Promise<void> {
    if (!this.isSupported()) return;

    this.applyConsentBanner();
  }

  /**
   * Apply consent banner (web-specific)
   */
  public applyConsentBanner(): void {
    if (!this.isSupported()) return;

    const isAnalyticsEnabled = this._analyticsService?.isEnabled?.();
    const isABTestingEnabled = this._abTestingService?.isEnabled();

    if (isAnalyticsEnabled || isABTestingEnabled) {
      this._injectConsentBanner();

      // Creating listener to be triggered when changes the route
      this._navigationService.listen(this._injectConsentBanner);
    }
  }

  /**
   * Update opt-in groups in ValueStore - web implementation
   */
  public async updateOptInGroupsInValueStore(): Promise<void> {
    const currentOptInGroups = (window as OptanonWindow)._O?.getOptInGroups();
    if (this._lastOptInGroups != currentOptInGroups) {
      await this._writeOptanonConsentInValueStore();
      this._lastOptInGroups = currentOptInGroups;

      // Emit consent updated event
      await this._emitConsentUpdatedEvent();
    }
  }

  /**
   * Emit consent updated event for other services to handle
   * @private
   */
  private async _emitConsentUpdatedEvent(): Promise<void> {
    try {
      const { optanonConsent } = getOptanonConsentObjectValues();

      const eventData: ConsentUpdateEventData = {
        source: 'optanon',
        optanonConsent,
        timestamp: Date.now()
      };

      if (this.onConsentUpdated) {
        await this.onConsentUpdated(eventData);
      }
    } catch (error) {
      internalLogger.error(
        'WebConsentStrategy: Failed to emit consent updated event:',
        error
      );
    }
  }

  private _shouldInjectConsentBanner(
    currentRoute: RouteServiceInputType
  ): boolean {
    return !(
      this._globalInjectConsentBanner === false ||
      currentRoute?.injectConsentBanner === false
    );
  }

  // This is specific for Web Strategy
  private _injectConsentBanner(): void {
    const currentPath: string = this._navigationService?.location?.pathname;
    const currentRoute: RouteServiceInputType =
      this._routesService.findRouteByPath(currentPath);

    // check if the current route has a configuration to explicit not inject consent banner.
    const shouldInjectConsentBanner =
      this._shouldInjectConsentBanner(currentRoute);

    if (!shouldInjectConsentBanner) {
      return;
    }

    const isLoginPath = LOGIN_PATHNAME === currentPath;
    const isLoggedinPath = LOGGEDIN_PATHNAME === currentPath;
    const isLoggedoutPath = LOGGEDOUT_PATHNAME === currentPath;

    const isLoggedinOrLoggedoutPath = isLoggedinPath || isLoggedoutPath;
    const isLoginPathWithoutEmailGathering =
      isLoginPath && !this._isEmailGathering;

    // not inject if the path is the loggedin or loggedout paths or the current path is the login path with no email gathering enabled.
    if (isLoggedinOrLoggedoutPath || isLoginPathWithoutEmailGathering) return;

    const stagePath = this._routeStageService.getStageByPath(currentPath);
    const isPrivateStagePathWithUserLoggedOut =
      stagePath && !stagePath?.public && !this?._sessionService?.isLoggedIn();

    const isPrivateRoutePathWithUserLoggedOut =
      currentRoute &&
      !currentRoute?.public &&
      !this?._sessionService?.isLoggedIn();

    // not inject if the current path is a stage or route path that is not public and the user is not logged in.
    if (
      isPrivateStagePathWithUserLoggedOut ||
      isPrivateRoutePathWithUserLoggedOut
    )
      return;
    internalLogger.debug?.('WebConsentStrategy: Injecting OneTrust banner.');
    injectOneTrust(this._applicationService.getPortalStack());
  }

  // TODO: in the future, check this use;
  private async _writeOptanonConsentInValueStore(): Promise<void> {
    const { optanonConsentId, optanonConsent } =
      getOptanonConsentObjectValues();

    // Emit consent updated event
    await this._emitConsentUpdatedEvent();

    await ValueStoreFacade.writeDataToValueStore([
      {
        key: ReservedValueStoreKeys.visitorUuid,
        value: optanonConsentId
      },
      {
        key: ReservedValueStoreKeys.webApplicationConsent,
        value: optanonConsent
      },
      {
        key: ReservedValueStoreKeys.applicationInstanceId,
        value: optanonConsentId
      }
    ]);
  }

  // TODO: in the future, check this use;
  private _registerOnDataLayer(): void {
    const writeOptanonConsentInValueStoreOnOptIn: EventHandlerType = {
      event: 'pb.onOptInGroups',
      callback: this._writeOptanonConsentInValueStore,
      groups: OPT_IN_GROUP_VALUE
    };

    const writeOptanonConsentInValueStoreOnOptOut: EventHandlerType = {
      event: 'pb.onOptInGroups',
      callback: this._writeOptanonConsentInValueStore,
      groups: OPT_OUT_GROUP_VALUE
    };

    registerOptanonDataLayerEventHandler(
      writeOptanonConsentInValueStoreOnOptIn
    );
    registerOptanonDataLayerEventHandler(
      writeOptanonConsentInValueStoreOnOptOut
    );
  }
}
